/**
 * CZNA-1523 — the magnifier behind a vocabulary quick pick (670 $a source citations).
 *
 * The quick pick only offers terms that BEGIN with the typed text, so a cataloguer who does
 * not know how an abbreviation starts never finds it: `Names` does not reach `LC (Names)`.
 * This modal browses the whole register instead — empty search lists every term, a typed
 * text matches anywhere in the term's name, case- and diacritics-insensitive.
 *
 * It filters the same term list the quick pick already holds, so searching costs no request.
 * A pick hands the term's text back; what happens to the value is the caller's business.
 * At most `MAX_LOOKUP_RESULTS` rows are drawn: NK's source list is ~200 terms, but the
 * register read goes up to the endpoint's offset cap, and 10 000 buttons freeze the modal.
 */
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, Button, Icon } from '@folio/stripes/components';
/** At most this many terms are listed; past it the cataloguer narrows the search. */
export var MAX_LOOKUP_RESULTS = 500;
/** Lower case without diacritics, so `ceske` finds `české`. */
var fold = function (text) { return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('cs'); };
/** Terms containing `typed` anywhere (case- and diacritics-insensitive), in register order. */
export var matchAnywhere = function (terms, typed) {
    var needle = fold(typed.trim());
    if (!needle)
        return terms;
    return terms.filter(function (term) { return fold(term).includes(needle); });
};
var listItemButtonStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    marginBottom: '4px',
    cursor: 'pointer',
    background: '#fff',
    font: 'inherit',
};
var searchInputStyle = {
    width: '100%',
    padding: '6px 8px',
    marginBottom: '12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    font: 'inherit',
    boxSizing: 'border-box',
};
var VocabularyLookupModal = function (_a) {
    var open = _a.open, onClose = _a.onClose, isLoading = _a.isLoading, isError = _a.isError, terms = _a.terms, onSelect = _a.onSelect;
    var intl = useIntl();
    var _b = useState(''), query = _b[0], setQuery = _b[1];
    var found = useMemo(function () { return matchAnywhere(terms, query); }, [terms, query]);
    var shown = found.slice(0, MAX_LOOKUP_RESULTS);
    var searchId = 'czna-vocabulary-lookup-search';
    var close = function () {
        setQuery('');
        onClose();
    };
    return (React.createElement(Modal, { open: open, onClose: close, label: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.modalTitle' }), dismissible: true, size: "small", footer: (React.createElement(Button, { onClick: close }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.close' }))) },
        React.createElement("label", { htmlFor: searchId, style: { display: 'block', marginBottom: '4px' } }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.searchLabel' })),
        React.createElement("input", { id: searchId, type: "search", autoComplete: "off", 
            // The cataloguer opened the modal to type into it.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus: true, value: query, onChange: function (e) { return setQuery(e.target.value); }, style: searchInputStyle }),
        isLoading && (React.createElement("div", { role: "status", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.loading' }), style: { textAlign: 'center', padding: '16px' } },
            React.createElement(Icon, { icon: "spinner-ellipsis" }))),
        !isLoading && isError && (React.createElement("div", { role: "alert", style: { color: '#d0021b', padding: '8px 0' } }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.error' }))),
        !isLoading && !isError && found.length === 0 && (React.createElement("p", { role: "status" }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.noResults' }))),
        !isLoading && found.length > MAX_LOOKUP_RESULTS && (React.createElement("p", { role: "status" }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.vocabularyLookup.truncated' }, { shown: MAX_LOOKUP_RESULTS, total: found.length }))),
        !isLoading && found.length > 0 && (React.createElement("ul", { style: { listStyle: 'none', margin: 0, padding: 0 }, "data-testid": "vocabulary-lookup-results" }, shown.map(function (term) { return (React.createElement("li", { key: term },
            React.createElement("button", { type: "button", style: listItemButtonStyle, onClick: function () {
                    setQuery('');
                    onSelect(term);
                } }, term))); })))));
};
export default VocabularyLookupModal;
